import { horizontal } from "../charts/horizontal";
import { vertical } from "../charts/vertical";
import { pie } from "../charts/pie/pie";
import { line } from "../charts/line";
import { verticalLine } from "../charts/vertical-line";
import { verticalStacked } from "../charts/vertical-stacked";
import { horizontalStacked } from "../charts/horizontal-stacked";
import { horizontalArea } from "../charts/horizontal-area";
import { verticalArea } from "../charts/vertical-area";

const lengthDataHandler = (rows: any[], columns: any[]) => {
    return {
        rowsLength: rows.length,
        columnsLength: columns.length
    }
};

const getVariableColor = (color: string | undefined) => {
    if (!color) return;
    const styles = getComputedStyle(document.body, null);
    if (!color.includes('var')) return color;
    const varColorValue = color.substring(
        color.lastIndexOf("(") + 1,
        color.lastIndexOf(")")
    )
    return styles.getPropertyValue(varColorValue).trim();
};

const groupArrays = (arr: any[][]) => {
    const maxLength = Math.max(...arr.map(subArr => subArr.length));
    const grouped: ({ value: string } | undefined | null)[][] = Array.from({ length: maxLength }, () => []);
    for (let i = 0; i < arr.length; i++) {
        const subArr = arr[ i ];
        for (let j = 0; j < subArr.length; j++) {
            grouped[ j ].push(subArr[ j ]);
        }
    }

    return grouped;
}

export const parser = (type: string, props: any = {}) => {
    //  ============ helpers =============
    const filtered = props.data?.filter((arr: any) => arr.some((val: any) => !!val));
    const colors = props.colors.map((color: string) => getVariableColor(color));
    props.color = getVariableColor(props.color);
    const primaryColor = getVariableColor('var(--theme-color-1)');
    //  ============ helpers =============


    // ======== Horizontal/Vertical bar chart ===========
    const barData = (data: any) => {
        const seriesTitles = data.shift().filter((data: any) => !!data).map((data: { value: string }) => data.value);
        const mapped: ({ value: string } | undefined | null)[][] = groupArrays(data);
        const filtered: any = mapped.filter(arr => arr.some(val => !!val));
        const titles: string[] = filtered.shift().map((val: any) => val?.value || '').filter((title: string) => title);
        let series: any = filtered
            .map((arr: any, i: number) => {
                return {
                    data: arr
                        .filter((data: any) => data && ![ undefined, '' ].includes(data.value) && String(data) !== '{}')
                        .map((value: any) => !value || isNaN(Number(value.value)) ? 0 : +value.value),
                    type: 'bar',
                    itemStyle: { color: colors[ i ] || primaryColor },
                    name: seriesTitles[ i ] || '',
                }
            })
            .filter((data: any) => data.data.length);

        if (series.length === 1) {
            series = [ {
                data: series[ 0 ].data.map((value: number, index: number) => {
                    return { value: value, itemStyle: { color: colors[ index ] || primaryColor } }
                }),
                type: 'bar',
                name: seriesTitles[ 0 ] || '',
            } ]
        }
        return {
            option: type === 'horizontal' ? horizontal(props, { titles, series }) : vertical(props, { titles, series }),
            ...lengthDataHandler(titles, series)
        }
    };
    // ======== Horizontal/Vertical bar chart ===========


    // ======== Pie/Donut chart ===========
    const pieData = (data: any) => {
        const seriesTitles = data.shift().filter((data: any) => !!data).map((data: { value: string }) => data.value);
        const mapped: ({ value: string } | undefined | null)[][] = groupArrays(data);
        const filtered: any = mapped.filter(arr => arr.some(val => !!val));
        const titles: string[] = filtered.shift().map((val: any) => val?.value || '').filter((title: string) => title);
        const isMobile: boolean = window.innerWidth <= 768;
        const series: any = filtered
            .map((arr: any, i: number) => {
                return {
                    name: seriesTitles[ i ] || '',
                    type: 'pie',
                    data: arr.filter((data: any) => data && data.value !== undefined)
                        .map((value: any, index: number) => {
                            const numb: number = !value || isNaN(Number(value.value)) ? 0 : +value.value;
                            return { value: numb, itemStyle: { color: colors[ index ] || primaryColor }, name: titles[ index ] || '' }
                        }),
                    radius: type === 'donut' ? [ `${props.donutSize || '70'}%`, '80%' ] : [ '0', '80%' ],
                    avoidLabelOverlap: true,
                    label: {
                        show: props.showAnnotation,
                        position: props.annotationPosition === 'outside' && !isMobile ? 'outside' : 'inside',
                        formatter: `${props.prefix ? props.prefix : ''}{c}${props.suffix ? props.suffix : ''}`,
                        fontSize: 12,
                        fontFamily: props.fontFamily,
                        fontWeight: props.annotationPosition === 'outside' && !isMobile ? 'normal' : 700
                    },
                    emphasis: {
                        itemStyle: {
                            shadowBlur: 10,
                            shadowOffsetX: 0,
                            shadowColor: 'rgba(0, 0, 0, 0.5)'
                        }
                    }
                }
            })
            .filter((data: any) => data.data.length).shift();

        return {
            option: pie(props, { series }),
            ...lengthDataHandler(titles, [ series ])
        }
    }
    // ======== Pie/Donut chart ===========

    // ======== Line/Area chart ===========
    const lineData = (data: any) => {
        const seriesTitles = data.shift().filter((data: any) => !!data).map((data: { value: string }) => data.value);
        const mapped: ({ value: string } | undefined | null)[][] = groupArrays(data);
        const filtered: any = mapped.filter(arr => arr.some(val => !!val));
        const titles: string[] = filtered.shift().map((val: any) => val?.value || '').filter((title: string) => title);

        const series: any = filtered
            .map((arr: any, i: number) => {
                const data: any = {
                    data: arr
                        .filter((data: any) => data && ![ undefined, '' ].includes(data.value))
                        .map((value: any) => !value || isNaN(Number(value.value)) ? 0 : +value.value),
                    type: 'line',
                    smooth: [ 'vertical-smooth-line', 'horizontal-smooth-line', 'vertical-smooth-area', 'horizontal-smooth-area' ].includes(type),
                    lineStyle: {
                        width: isNaN(props.lineSize) ? 1 : Number(props.lineSize)
                    },
                    itemStyle: { color: colors[ i ] || primaryColor },
                    name: seriesTitles[ i ] || '',
                }

                if ([ 'vertical-area', 'vertical-smooth-area', 'horizontal-area', 'horizontal-smooth-area' ].includes(type)) {
                    data[ 'areaStyle' ] = {}
                }

                return data;
            })
            .filter((data: any) => data.data.length);

        const options: any = {
            'line': line(props, { titles, series }),
            'horizontal-smooth-line': line(props, { titles, series }),
            'vertical-line': verticalLine(props, { titles, series }),
            'vertical-smooth-line': verticalLine(props, { titles, series }),
            'horizontal-area': horizontalArea(props, { titles, series }),
            'horizontal-smooth-area': horizontalArea(props, { titles, series }),
            'vertical-area': verticalArea(props, { titles, series }),
            'vertical-smooth-area': verticalArea(props, { titles, series }),
        }

        return {
            forceColumns: series.length === 1,
            option: options[ type ],
            ...lengthDataHandler(titles, series)
        }
    }
    // ======== Line/Area chart ===========


    // ======== Horizontal/Vertical stacked chart ===========
    const stackedData = (data: any) => {
        const seriesTitles = data.shift().filter((data: any) => !!data).map((data: { value: string }) => data.value);
        const mapped: ({ value: string } | undefined | null)[][] = groupArrays(data);
        const filtered: any = mapped.filter(arr => arr.some(val => !!val));
        const titles: string[] = filtered.shift().map((val: any) => val?.value || '').filter((title: string) => title);
        let series: any = filtered
            .map((arr: any, i: number) => {
                return {
                    data: arr
                        .filter((data: any) => data && ![ undefined, '' ].includes(data.value) && String(data) !== '{}')
                        .map((value: any) => !value || isNaN(Number(value.value)) ? 0 : +value.value),
                    type: 'bar',
                    stack: 'total',
                    itemStyle: { color: colors[ i ] || primaryColor },
                    name: seriesTitles[ i ] || '',
                    emphasis: {
                        focus: 'series'
                    }
                }
            })
            .filter((data: any) => data.data.length);

        if (series.length === 1) {
            series = [ {
                data: series[ 0 ].data.map((value: number, index: number) => {
                    return { value: value, itemStyle: { color: colors[ index ] || primaryColor } }
                }),
                type: 'bar',
                stack: 'total',
                name: seriesTitles[ 0 ] || '',
            } ]
        }
        return {
            option: type === 'vertical-stacked' ? verticalStacked(props, { titles, series }) : horizontalStacked(props, { titles, series }),
            ...lengthDataHandler(titles, series)
        }
    };
    // ======== Horizontal/Vertical stacked chart ===========

    const handler: any = {
        'horizontal': () => barData(filtered),
        'vertical': () => barData(filtered),
        'pie': () => pieData(filtered),
        'donut': () => pieData(filtered),
        'line': () => lineData(filtered),
        'vertical-line': () => lineData(filtered),
        'vertical-smooth-line': () => lineData(filtered),
        'horizontal-smooth-line': () => lineData(filtered),
        'vertical-area': () => lineData(filtered),
        'horizontal-area': () => lineData(filtered),
        'vertical-smooth-area': () => lineData(filtered),
        'horizontal-smooth-area': () => lineData(filtered),
        'stacked-horizontal': () => stackedData(filtered),
        'vertical-stacked': () => stackedData(filtered),
        'horizontal-stacked': () => stackedData(filtered),
    }

    return typeof handler[ type ] === 'function' ? handler[ type ]() : {};
}
